/*
TRANE CONFIDENTIAL

2021 – 2022 Copyright Trane U.S. Inc.
All Rights Reserved.

NOTICE: All information contained herein is, and remains the property of
Trane U.S. Inc. The source code, object code, intellectual and technical
concepts contained herein are proprietary to Trane U.S. Inc. and may be
covered by patents and patent applications and are protected by trade secret
and copyright law. Dissemination of this information or reproduction of this
material is strictly forbidden unless prior written permission is obtained
from Trane U.S. Inc. Do not use this software without the prior written
permission of Trane U.S. Inc.
*/

import * as React from "react";
import PropTypes from "prop-types";
import { Redirect, Route, matchPath } from "react-router-dom";
import AppContainer from "nx-components/AppContainer";
import SwitchWith404 from "nexia_mobile/components/Routing/SwitchWith404";
import InternetRequiredRoute from "nexia_mobile/components/Routing/InternetRequiredRoute";
import MainMenu from "nexia_mobile/components/MainMenu";
import { history, clearHistoryAndPush, goBackTo } from "nexia_mobile/history";
import { DrawerRevealerProvider } from "nexia_mobile/components/TopBar/DrawerRevealer";
import CurrentLocationCollectionProvider from "nexia_mobile/components/CurrentLocationCollectionProvider";
import Dispatch from "nexia_mobile/flux_like/dispatch";
import Store from "nexia_mobile/flux_like/store";
import connect from "nexia_mobile/flux_like/store/connect";
import { getPendingPromiseForRefreshLocationAndReturnToDashboard } from "nexia_mobile/flux_like/action_handlers/location";
import { getPendingPromiseForRefreshSessionAndReturnToDashboard } from "nexia_mobile/flux_like/action_handlers/session";

import HomePage from "nexia_mobile/components/Pages/HomePage";
import BuildingRoutes from "nexia_mobile/components/Routing/BuildingRoutes";
import DeviceDetails from "nexia_mobile/components/Details/DeviceDetails";
import OccupancySetpointsPage from "nexia_mobile/components/Pages/OccupancySetpointsPage";

import { makeLoaderComponent } from "nexia_mobile/components/Routing/Loading";

const DeviceEnrollmentPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/DeviceEnrollmentPage")
);
const Alerts = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/Alerts")
);
const AutomationRoutes = makeLoaderComponent(() =>
  import("nexia_mobile/components/Routing/AutomationRoutes")
);
const EventsPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/EventsPage")
);
const RecordedVideoPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/RecordedVideoPage")
);
const PhoneRoutes = makeLoaderComponent(() =>
  import("nexia_mobile/components/Routing/PhoneRoutes")
);
const HomeSettings = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/HomeSettings")
);
const OpenSourceLicensePage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/OpenSourceLicensePage")
);
const CreateGroupPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/CreateGroupPage")
);
const EditGroupPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/EditGroupPage")
);
const VideoFeedPlayerPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/Video/VideoFeedPlayerPage")
);
const MultiVideoFeedPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/Video/MultiVideoFeedPage")
);
const SchemaEditPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/SchemaEditPage")
);
const LocalSettingsPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/LocalSettingsPage")
);
const SupportPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/SupportPage")
);
const XR724EnrollmentWizard = makeLoaderComponent(() =>
  import("nexia_mobile/components/Wizard/XR724EnrollmentWizard")
);
const XR724WifiConfigurationWizard = makeLoaderComponent(() =>
  import("nexia_mobile/components/Wizard/XR724WifiConfigurationWizard")
);
const DragonflyEnrollmentWizard = makeLoaderComponent(() =>
  import("nexia_mobile/components/Wizard/DragonflyEnrollmentWizard")
);
const DragonflyWifiConfigurationWizard = makeLoaderComponent(() =>
  import("nexia_mobile/components/Wizard/DragonflyWifiConfigurationWizard")
);
const DeviceSettingsPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/DeviceSettingsPage")
);
const ThermostatAlertsPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/ThermostatAlertsPage")
);
const ThermostatFaultAlertsPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/ThermostatFaultAlertsPage")
);
const ThermostatHighLowAlertsPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/ThermostatHighLowAlertsPage")
);
const ThermostatHumidityAlertsPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/ThermostatHumidityAlertsPage")
);
const OtherTemperatureAlertsPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/OtherTemperatureAlertsPage")
);
const BinaryInputAlertPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/BinaryInputAlertPage")
);
const PinAccessPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/PinAccessPage")
);
const AccessRestrictionPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/AccessRestrictionPage")
);
const DeviceAdvancedInfoPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/DeviceAdvancedInfoPage")
);
const UserCodes = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/UserCodes")
);
const TemporaryCodeEdit = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/TemporaryCodeEdit")
);
const ThermostatScheduleEditPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/ThermostatScheduleEditPage")
);
const RuntimeHistoryPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/RuntimeHistoryPage")
);

class AppRoutes extends React.Component {
  static propTypes = {
    isPlayingVideo: PropTypes.bool.isRequired,
  };

  state = {
    isDrawerRequested: false,
  };

  render() {
    const { isPlayingVideo } = this.props;
    const { isDrawerRequested } = this.state;

    return (
      <DrawerRevealerProvider
        onDrawerRevealerTap={() => {
          this.setState((prevState) => ({
            isDrawerRequested: !prevState.isDrawerRequested,
          }));
        }}
      >
        <AppContainer
          drawerChildren={
            <MainMenu
              itemIsSelected={(id) => {
                const { location } = history;
                const computedMatch = matchPath(location.pathname, {
                  path: `/app/${id}`,
                });
                return Boolean(computedMatch);
              }}
              onItemSelected={() => {
                this.setState({ isDrawerRequested: false });
              }}
            />
          }
          isDrawerRequested={isDrawerRequested}
          fullscreen={isPlayingVideo}
          onDrawerClose={() => this.setState({ isDrawerRequested: false })}
          onDrawerOpen={() => this.setState({ isDrawerRequested: true })}
        >
          <SwitchWith404>
            <Route
              path="/app"
              exact
              render={() => {
                if (Store.vognition.listenOnStartupPending) {
                  Dispatch("START_VOGNITION_LISTENER_FROM_STARTUP");
                }

                return <Redirect to="/app/home" />;
              }}
            />
            <InternetRequiredRoute
              path="/app/home"
              exact
              render={() => (
                <HomePage
                  showDeviceAdd={() => {
                    clearHistoryAndPush("/app/devices/add");
                  }}
                />
              )}
            />
            <InternetRequiredRoute
              path="/app/buildings"
              render={() => <BuildingRoutes />}
            />

            <InternetRequiredRoute
              path="/app/devices/details"
              exact
              render={({ location }) => {
                const { device } = location.state;
                return <DeviceDetails model={device} />;
              }}
            />
            <InternetRequiredRoute
              path="/app/devices/schedule"
              exact
              render={({ location }) => {
                const { device, schedule } = location.state;
                return (
                  <ThermostatScheduleEditPage
                    device={device}
                    schedule={schedule}
                  />
                );
              }}
            />
            <InternetRequiredRoute
              path="/app/devices/runtime-history"
              exact
              render={({ location }) => {
                const { device } = location.state;
                return <RuntimeHistoryPage model={device} />;
              }}
            />
            <InternetRequiredRoute
              path="/app/devices/settings"
              exact
              render={({ location }) => {
                const { device } = location.state;

                return <DeviceSettingsPage device={device} />;
              }}
            />
            <InternetRequiredRoute
              path="/app/devices/user-codes"
              // Not exact because UserCodes has nested routes
              render={({ location }) => {
                const { device } = location.state;

                return <UserCodes model={device} />;
              }}
            />
            <InternetRequiredRoute
              path="/app/devices/temporary-code-edit"
              exact
              render={({ location }) => {
                const { device } = location.state;

                return <TemporaryCodeEdit model={device} />;
              }}
            />
            <InternetRequiredRoute
              path="/app/devices/add"
              // Not exact because DeviceEnrollmentPage has nested routes
              render={() => (
                <DeviceEnrollmentPage
                  onWizardFinish={() => {
                    Dispatch("REFRESH_LOCATION_AND_RETURN_TO_DASHBOARD");
                    return getPendingPromiseForRefreshLocationAndReturnToDashboard();
                  }}
                />
              )}
            />
            <InternetRequiredRoute
              path="/app/devices/occupancy-setpoints"
              exact
              render={({ location }) => {
                const { device } = location.state;
                return <OccupancySetpointsPage model={device} />;
              }}
            />
            <InternetRequiredRoute
              path="/app/devices/thermostat-alerts"
              exact
              render={({ location }) => {
                const { device } = location.state;
                return <ThermostatAlertsPage model={device} />;
              }}
            />
            <InternetRequiredRoute
              path="/app/devices/thermostat-alerts/fault"
              exact
              render={({ location }) => {
                const { device } = location.state;
                return <ThermostatFaultAlertsPage model={device} />;
              }}
            />
            <InternetRequiredRoute
              path="/app/devices/thermostat-alerts/highlowfault"
              exact
              render={({ location }) => {
                const { device } = location.state;
                return <ThermostatHighLowAlertsPage model={device} />;
              }}
            />
            <InternetRequiredRoute
              path="/app/devices/thermostat-alerts/humidityfault"
              exact
              render={({ location }) => {
                const { device } = location.state;
                return <ThermostatHumidityAlertsPage model={device} />;
              }}
            />
            <InternetRequiredRoute
              path="/app/devices/thermostat-alerts/othertemperature"
              exact
              render={({ location }) => {
                const { device, giValue } = location.state;
                return (
                  <OtherTemperatureAlertsPage
                    model={device}
                    giValue={giValue}
                  />
                );
              }}
            />
            <InternetRequiredRoute
              path="/app/devices/thermostat-alerts/binaryinputalert"
              exact
              render={({ location }) => {
                const { device, giValue } = location.state;
                return (
                  <BinaryInputAlertPage model={device} giValue={giValue} />
                );
              }}
            />
            <InternetRequiredRoute
              path="/app/devices/pin-access"
              exact
              render={({ location }) => {
                const { device } = location.state;
                return <PinAccessPage model={device} />;
              }}
            />
            <InternetRequiredRoute
              path="/app/devices/access-restriction"
              exact
              render={({ location }) => {
                const { device } = location.state;
                return <AccessRestrictionPage model={device} />;
              }}
            />
            <InternetRequiredRoute
              path="/app/devices/advanced"
              exact
              render={({ location }) => {
                const { device } = location.state;
                return <DeviceAdvancedInfoPage device={device} />;
              }}
            />
            <InternetRequiredRoute
              path="/app/alerts"
              // Not exact because Alerts has nested routes
              render={() => <Alerts />}
            />
            <InternetRequiredRoute
              path="/app/automations"
              // Not exact because AutomationRoutes has nested routes
              render={() => <AutomationRoutes />}
            />
            <InternetRequiredRoute
              path="/app/events"
              exact
              render={({ location }) => {
                const filterHref =
                  location && location.state ? location.state.filterHref : null;
                return (
                  <CurrentLocationCollectionProvider collectionName="events">
                    {(collection) => (
                      <EventsPage
                        collection={collection}
                        filterHref={filterHref}
                      />
                    )}
                  </CurrentLocationCollectionProvider>
                );
              }}
            />
            <InternetRequiredRoute
              path="/app/videos"
              exact
              render={({ location }) => {
                const filterHref =
                  location && location.state ? location.state.filterHref : null;
                return (
                  <CurrentLocationCollectionProvider collectionName="recordedVideos">
                    {(collection) => (
                      <RecordedVideoPage
                        collection={collection}
                        filterHref={filterHref}
                        loadOnMount={history.action !== "POP"}
                      />
                    )}
                  </CurrentLocationCollectionProvider>
                );
              }}
            />
            <InternetRequiredRoute
              path="/app/videos/edit"
              exact
              render={({ location }) => {
                const { editSchema, onCommitted, onDeleted } = location.state;

                return (
                  <SchemaEditPage
                    schema={editSchema}
                    onCommitted={onCommitted}
                    onDeleted={onDeleted}
                  />
                );
              }}
            />
            <InternetRequiredRoute
              path="/app/phones"
              // Not exact because PhoneRoutes has nested routes
              render={() => <PhoneRoutes />}
            />
            <Route
              path="/app/settings"
              exact
              render={() => (
                <LocalSettingsPage
                  refreshLocation={() => {
                    Dispatch("REFRESH_SESSION_AND_RETURN_TO_DASHBOARD");
                    return getPendingPromiseForRefreshSessionAndReturnToDashboard();
                  }}
                />
              )}
            />
            <InternetRequiredRoute
              path="/app/settings/home-settings"
              exact
              render={() => <HomeSettings />}
            />
            <Route path="/app/support" exact render={() => <SupportPage />} />
            <Route
              path="/app/open-source-license-info"
              exact
              render={() => <OpenSourceLicensePage />}
            />
            <InternetRequiredRoute
              path="/app/groups/add"
              exact
              render={() => (
                <CreateGroupPage
                  onCommitted={() => {
                    Dispatch("REFRESH_LOCATION_AND_RETURN_TO_DASHBOARD");
                    return getPendingPromiseForRefreshLocationAndReturnToDashboard();
                  }}
                />
              )}
            />
            <InternetRequiredRoute
              path="/app/groups/edit"
              exact
              render={({ location }) => {
                const { editSchema } = location.state;

                return (
                  <EditGroupPage
                    editSchema={editSchema}
                    onCommitted={() => {
                      Dispatch("REFRESH_LOCATION_AND_RETURN_TO_DASHBOARD");
                      return getPendingPromiseForRefreshLocationAndReturnToDashboard();
                    }}
                    onDeleted={() => {
                      Dispatch("REFRESH_LOCATION_AND_RETURN_TO_DASHBOARD");
                      return getPendingPromiseForRefreshLocationAndReturnToDashboard();
                    }}
                  />
                );
              }}
            />
            <InternetRequiredRoute
              path="/app/video-feed"
              exact
              render={({ location }) => {
                const { model } = location.state;

                return (
                  <VideoFeedPlayerPage
                    model={model}
                    onRequestClose={() => {
                      history.goBack();
                    }}
                  />
                );
              }}
            />
            <InternetRequiredRoute
              path="/app/multi-video-feed"
              exact
              render={({ location }) => {
                const { group } = location.state;

                return <MultiVideoFeedPage group={group} />;
              }}
            />
            <Route
              path="/app/xr724/enrollment-wizard"
              // Not exact because XR724EnrollmentWizard has nested routes
              render={({ location }) => {
                const {
                  wizardVideos,
                  fetchEnrollmentDetails,
                  onWizardFinish,
                  onWizardLeave,
                } = location.state;

                return (
                  <XR724EnrollmentWizard
                    wizardVideos={wizardVideos}
                    fetchEnrollmentDetails={fetchEnrollmentDetails}
                    onWizardFinish={onWizardFinish}
                    onWizardLeave={onWizardLeave}
                    exitWizard={onWizardFinish}
                    defaultWizardState={{
                      detailsHaveBeenFetched: false,
                      pin: null,
                      statusUrl: null,
                      cancelLink: null,
                      wizardExpiresAt: null,
                      userKey: null,
                      networks: null,
                      chosenNetwork: null,
                      errorMessage: null,
                      isErrorRecoverable: true,
                    }}
                  />
                );
              }}
            />
            <Route
              path="/app/xr724/wifi-configuration-wizard"
              // Not exact because XR724WifiConfigurationWizard has nested routes
              render={({ location }) => {
                const {
                  fetchDeviceLastUpdateTime,
                  wizardVideos,
                  onWizardFinish,
                  onWizardLeave,
                  defaultWizardState,
                } = location.state;
                return (
                  <XR724WifiConfigurationWizard
                    wizardVideos={wizardVideos}
                    fetchDeviceLastUpdateTime={fetchDeviceLastUpdateTime}
                    onWizardFinish={onWizardFinish}
                    onWizardLeave={onWizardLeave}
                    defaultWizardState={defaultWizardState}
                  />
                );
              }}
            />
            <Route
              path="/app/dragonfly/enrollment-wizard"
              // Not exact because DragonflyEnrollmentWizard has nested routes
              render={({ location }) => {
                const {
                  fetchEnrollmentDetails,
                  onWizardFinish,
                } = location.state;

                return (
                  <DragonflyEnrollmentWizard
                    fetchEnrollmentDetails={fetchEnrollmentDetails}
                    onWizardFinish={onWizardFinish}
                    exitWizard={onWizardFinish}
                    defaultWizardState={{
                      detailsHaveBeenFetched: false,
                      pin: null,
                      statusUrl: null,
                      cancelLink: null,
                      wizardExpiresAt: null,
                      networks: null,
                      chosenNetwork: null,
                      errorMessage: null,
                      isErrorRecoverable: true,
                    }}
                  />
                );
              }}
            />
            <Route
              path="/app/dragonfly/wifi-configuration-wizard"
              // Not exact because DragonflyWifiConfigurationWizard has nested routes
              render={({ location }) => {
                const { device } = location.state;
                return (
                  <DragonflyWifiConfigurationWizard
                    fetchDeviceLastUpdateTime={() =>
                      device.fetchLastUpdateTime()
                    }
                    onWizardFinish={() => {
                      goBackTo("/app/devices/settings");
                    }}
                    defaultWizardState={{
                      networks: null,
                      chosenNetwork: null,
                    }}
                  />
                );
              }}
            />
          </SwitchWith404>
        </AppContainer>
      </DrawerRevealerProvider>
    );
  }
}

export default connect({ isPlayingVideo: "isPlayingVideo" }, AppRoutes);



// WEBPACK FOOTER //
// ./src/components/Routing/AppRoutes.jsx